import * as React from 'react'
import { useCallback, useEffect, useState } from 'react'
import { useTheme } from '@mui/material/styles'
import { LocationsButtonProps } from '../typings'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import { useTidalThemeActive } from '@user/features/TidalThemeToggle'
import { CreateLocationModal } from '@user/components/Location/CreateLocationModal'
import { getStyles } from './stylings'
import { LocationsHeader } from './LocationsHeader'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import { LocationAvatar } from '@sales-center/components'
import { useCreateLocations } from './hooks/useCreateLocations'
import { createLocationTitle } from '../constants'
import LinearProgress from '@mui/material/LinearProgress'
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUp from '@mui/icons-material/KeyboardArrowUp'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'

export const LocationsButton: React.FC<LocationsButtonProps> = (props) => {
  const {
    open,
    onOpen,
    onClose,
    matched,
    listViewHeader,
    variant = 'text',
  } = props

  const [disabled, setDisabled] = useState(false)
  const [addLocToPestPac, setAddLocToPestPac] = useState(false)
  const onClick = open ? onClose : onOpen

  const {
    entityId,
    entityType,
    openNewLocation,
    onCloseNewLocation,
    handleCreateNewLocation,
    addToPestPac,
    saving,
    canCreateLocations,
    errors,
  } = useCreateLocations()

  const tidalThemeActive = useTidalThemeActive()

  useEffect(() => {
    if (saving) {
      setDisabled(true)
    } else {
      if (errors) {
        setDisabled(false)
      }
    }
  }, [saving, setDisabled, errors])

  const onPestPacClick = useCallback(
    (e) => {
      setDisabled(true)
      setAddLocToPestPac(true)
      addToPestPac()

      e.stopPropagation()
    },
    [setDisabled, addToPestPac],
  )

  const onCloseLocation = useCallback(() => {
    onCloseNewLocation()
    setDisabled(false)
  }, [onCloseNewLocation, setDisabled])

  const scAvatar = listViewHeader ? undefined : (
    <LocationAvatar matched={matched} size={variant === 'text' ? 28 : 22} />
  )

  const matchedAvatar = listViewHeader ? undefined : (
    <Box mt={0.4} height={'24px'} width={'24px'}>
      <img src={'/static-assets/icons/pest-pac-icon.svg'} />
    </Box>
  )

  const headerText = listViewHeader ? listViewHeader : 'Bill-to / Location'
  const icon = open ? <KeyboardArrowUp /> : <KeyboardArrowDown />

  const styles = getStyles(
    props,
    useTheme(),
    tidalThemeActive,
    canCreateLocations,
  )

  const header = (
    <>
      <span style={styles.textHeader}>{headerText}</span>
      <IconButton color="primary" size="small">
        {icon}
      </IconButton>
    </>
  )

  return (
    <Stack direction="column" alignItems="flex-start">
      <Box display="flex" alignItems="center" mt={'14px'} pl={'0px'}>
        {variant === 'text' ? (
          <Box
            display="flex"
            alignItems="center"
            style={{ cursor: 'pointer' }}
            onClick={onClick}
            data-test-id={`locations-button-${entityId}`}
          >
            {scAvatar}
            <Typography>{header}</Typography>
          </Box>
        ) : (
          <Chip
            sx={styles.chip}
            variant="outlined"
            label={
              <LocationsHeader
                {...props}
                canCreateLocations={canCreateLocations}
              />
            }
            onClick={onClick}
            data-test-id={`locations-button-${entityId}`}
            avatar={
              matched
                ? matchedAvatar
                : !canCreateLocations
                  ? scAvatar
                  : undefined
            }
          />
        )}
        {!matched && canCreateLocations && variant === 'chip' && (
          <>
            <Box>
              <Button
                disableElevation
                sx={styles.button}
                variant="contained"
                onClick={onPestPacClick}
                disabled={disabled}
                data-test-id={`add-to-pestpac-${entityId}`}
              >
                <Typography variant="body2" sx={styles.buttonTitle}>
                  Add to PestPac
                </Typography>
              </Button>
            </Box>
          </>
        )}
      </Box>

      {saving && (
        <Box mt={0} ml={'11px'} flexDirection="column" display="flex">
          <LinearProgress
            color="primary"
            sx={{ width: tidalThemeActive ? '250px' : '235px', height: '2px' }}
          />
        </Box>
      )}

      <CreateLocationModal
        open={openNewLocation}
        onClose={onCloseLocation}
        entityId={entityId}
        entityType={entityType}
        title={createLocationTitle}
        tidalThemeActive={tidalThemeActive}
        onNewLocationsSave={handleCreateNewLocation}
        addToPestPac={addLocToPestPac}
      />
    </Stack>
  )
}
LocationsButton.displayName = 'LocationsButton'
